import { useEffect, useMemo, useRef, useState } from 'react';
import { Notification } from '../../api';
import { toast } from '@/components/ui/use-toast';

interface UseOpenMarketNotificationsProps {
	eventId: string | undefined;
	lobbyResponse: any;
	isCrowdLockMode: boolean;
	isRegisteredUser: boolean;
	slatesData: any[];
}

export function useOpenMarketNotifications({
	eventId,
	lobbyResponse,
	isCrowdLockMode,
	isRegisteredUser,
	slatesData,
}: UseOpenMarketNotificationsProps) {
	const [notificationsListOpenMarket, setNotificationsListOpenMarket] = useState<any[]>([]);
	const hasFetchedRef = useRef(false);

	useEffect(() => {
		if (!eventId || !lobbyResponse || isCrowdLockMode || hasFetchedRef.current) return;

		const fetchOpenMarketNotifications = async () => {
			try {
				const data = isRegisteredUser ? await Notification.getListOpenMarket(eventId) : [];
				if (data.length > 0) {
					setNotificationsListOpenMarket(data);
				}
				hasFetchedRef.current = true;
			} catch (error) {
				console.error('Error fetching open market notifications:', error);
				toast({
					variant: 'destructive',
					title: 'Error',
					description: 'Could not load open market notifications.',
				});
			}
		};

		fetchOpenMarketNotifications();
	}, [eventId, lobbyResponse, isCrowdLockMode, isRegisteredUser]);

	const matchOpeningDetails = useMemo(() => {
		if (!slatesData || !notificationsListOpenMarket) return [];

		const notifiedMatchIds = new Set(
			notificationsListOpenMarket.map((notification) => notification.match_id)
		);

		return slatesData.flatMap((slate) =>
			(slate.matches || [])
				.filter(
					(match: any) => !!match?.opening_description?.trim() && !notifiedMatchIds.has(match.id)
				)
				.map((match: any) => ({
					id: match.id,
					name: match.name,
					opening_description: match.opening_description.trim(),
				}))
		);
	}, [slatesData, notificationsListOpenMarket]);

	const handleSubscribeToOpenMarket = async (match_id: string) => {
		try {
			await Notification.subscribeToOpenMarket(match_id);

			toast({
				title: 'Success',
				description: 'You have successfully subscribed to open market updates!',
			});

			setNotificationsListOpenMarket((prev) => [...prev, { id: match_id, match_id }]);
		} catch (error) {
			console.error('Subscription error:', error);

			toast({
				variant: 'destructive',
				title: 'Error',
				description: 'Failed to subscribe to open market. Check the console for details.',
			});

			throw new Error('Unexpected error during subscription process');
		}
	};

	return { matchOpeningDetails, handleSubscribeToOpenMarket };
}
